import React, { useState, useEffect, useContext } from "react";
import { Controller, useForm } from "react-hook-form";
import { LoadingPanel } from "../LoadingPanel/LoadingPanel";
import { reloadOptions } from "../../request/api";
import { sendLoadingPostRequest } from "../../request/request";
import {
  TextField,
  ThemeProvider,
  createTheme,
  styled,
  Autocomplete,
} from "@mui/material";
import FormContext from "../../contexts/FormContext";
import "./StepPPP.css";
import { SurveyMainType } from "../../types/SurveyMainType";
import { GenerateQuestion } from "../Surveys/GenerateQuestion";
import { IStep2FormInput } from "../../types/IStep2FormInput";
import { DictionaryCombo } from "../../types/DictionaryCombo";
import { Dictionary } from "../../types/Dictionary";
import { SurveyListType } from "../../types/SurveyListType";
import { PPPType } from "../../types/PPPType";
import { Beneficiary } from "../Beneficiary/Beneficiary";
import { CheckHighRisk } from "../../types/CheckHighRisk";

const TextFieldStyled = styled(TextField)(({ theme }) => ({
  width: "100%",
  "& .MuiOutlinedInput-root .MuiAutocomplete-input:first-child": {
    height: 40,
  },
  "& .MuiInputLabel-shrink, & fieldset": {
    fontSize: "13px",
  },
  "& .MuiOutlinedInput-root": {
    height: 50,
  },
}));

const defaultMaterialTheme = createTheme({
  palette: {
    primary: {
      main: "#000000",
    },
  },
  components: {
    MuiInputLabel: {
      styleOverrides: {
        root: {
          "&.MuiInputLabel-marginDense": {
            transform: "translate(14px, 20px) scale(1)",
          },
          "&.MuiInputLabel-shrink": {
            transform: "translate(14px, -6px) scale(0.75) !important",
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          display: "none",
        },
      },
    },
  },
});

interface IStepPPPFormInput {
  sourceOfAssetValues: string[];
}

interface IStep0Form {
  setStep: React.Dispatch<React.SetStateAction<number>>;
}

const StepPPP = ({ setStep }: IStep0Form) => {
  const context = useContext(FormContext);
  const [loading, setLoading] = useState<boolean>(false);
  const [pppData, setPppData] = useState<SurveyMainType>();
  const [highRiskPppData, setHighRiskPppData] = useState<SurveyMainType>();
  const [isHighRisk, setIsHighRisk] = useState<boolean>();
  const [canCheck, setCanCheck] = useState<boolean>(false);
  const [dictionary, setDictionary] = useState<Dictionary>();
  const [errorIds, setErrorIds] = useState<number[]>();
  const [beneficiaryErrors, setBeneficiaryErrors] = useState<string[]>();
  const [nextClicked, setNextClicked] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
    getValues,
    setValue,
    clearErrors,
    setError,
    trigger,
  } = useForm<IStep2FormInput>({
    defaultValues: context.step2,
    mode: "onChange",
    shouldFocusError: false,
  });

  const scrollToElement = (name: string) => {
    let firstErrorElement: HTMLElement | null =
      document.getElementsByName(name)[0];
    if (!firstErrorElement) {
      firstErrorElement = document.getElementById(name);
    }
    if (firstErrorElement) {
      firstErrorElement.scrollIntoView({ behavior: `smooth`, block: "center" });
      firstErrorElement.scrollIntoView({ behavior: `smooth`, block: "center" });
    }
  };

  const isEmpty = (text?: string) => {
    if (text == null || text === "") return true;
    return false;
  };

  const getDictionary = async () => {
    reloadOptions();
    const { data: response, success } = await sendLoadingPostRequest<
      never,
      Dictionary
    >({
      endpoint: "Dictionary/GetAll",
    });
    if (success && response?.data) {
      setDictionary(response?.data);
    }
  };

  const getPPP = async () => {
    reloadOptions();
    const { data: response, success } = await sendLoadingPostRequest<
      never,
      SurveyListType
    >({
      loadingFunction: setLoading,
      endpoint: "agreement/GetPPP",
    });
    if (success && response?.data) {
      const resp = response?.data;
      if (resp == null) return;
      setPppData(resp.survey);
      setHighRiskPppData(resp.surveyHighRisk);
      setIsHighRisk(resp.isHighRisk);
    }
  };

  const compareDictionary = (a: DictionaryCombo, b: DictionaryCombo) => {
    if (a.name < b.name) {
      return -1;
    }
    if (a.name > b.name) {
      return 1;
    }
    return 0;
  };

  const getError = (data: SurveyMainType, checkTouched: boolean) => {
    const localErrorIds: number[] = [];
    data?.questions.forEach((question) => {
      if (
        question.isRequired &&
        question.type !== 1 &&
        (checkTouched || question.isTouched)
      ) {
        if (
          question.answers?.filter(
            (ans) =>
              (ans.isSelected && !ans.showTextBox) ||
              (ans.isSelected &&
                ans.showTextBox &&
                !isEmpty(ans.additionalText)),
          ).length === 0
        ) {
          localErrorIds.push(question.id);
        }
      }
    });
    return localErrorIds;
  };

  const validateDO = (numer: string | undefined) => {
    if (numer == null || numer.length != 9) return false;

    numer = numer.toUpperCase();
    const letterValues = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ";

    for (let i = 0; i < 3; i++)
      if (
        letterValues.indexOf(numer[i]) < 10 ||
        numer[i] == "O" ||
        numer == "Q"
      )
        return false;

    for (let i = 3; i < 9; i++)
      if (
        letterValues.indexOf(numer[i]) < 0 ||
        letterValues.indexOf(numer[i]) > 9
      )
        return false;

    let sum =
      7 * letterValues.indexOf(numer[0]) +
      3 * letterValues.indexOf(numer[1]) +
      1 * letterValues.indexOf(numer[2]) +
      7 * letterValues.indexOf(numer[4]) +
      3 * letterValues.indexOf(numer[5]) +
      1 * letterValues.indexOf(numer[6]) +
      7 * letterValues.indexOf(numer[7]) +
      3 * letterValues.indexOf(numer[8]);
    sum %= 10;
    if (sum != letterValues.indexOf(numer[3])) return false;

    return true;
  };

  const validatePassport = (number: string) => {
    const tab: number[] = [7, 3, 9, 1, 7, 3, 1, 7, 3];

    number = number.trim().replace(/\s+/g, "");

    if (number.length !== 9) {
      return false;
    }

    let charCode: number;

    for (let i = 0; i < 2; i++) {
      charCode = number.charCodeAt(i);
      if (charCode < 65 || charCode > 90) {
        return false;
      }
    }

    for (let i = 2; i < 9; i++) {
      charCode = number.charCodeAt(i);
      if (charCode < 48 || charCode > 57) {
        return false;
      }
    }

    let sum = 0;

    for (let i = 0; i < 9; i++) {
      if (i < 2) {
        // Dla liter: A=10, B=11, ..., Z=35
        sum += (number.charCodeAt(i) - 55) * tab[i];
      } else {
        sum += parseInt(number[i], 10) * tab[i];
      }
    }

    return sum % 10 === 0;
  };

  const checkErrors = (checkTouched: boolean) => {
    const localErrorIds: number[] = [];
    const clicked = checkTouched === true || nextClicked === true;

    if (pppData) {
      localErrorIds.push.apply(localErrorIds, getError(pppData, clicked));
    }

    // if(isHighRisk && highRiskPppData != null) {
    //     localErrorIds.push.apply(localErrorIds, (getError(highRiskPppData, checkTouched)));
    // }

    setErrorIds(localErrorIds.length > 0 ? localErrorIds : []);
    return localErrorIds;
  };

  const checkBeneficiary = () => {
    const errorsList: string[] = [];

    if (context.ppp?.withBeneficiary == null) {
      errorsList.push("main");
    }

    if (
      context.ppp?.withBeneficiary &&
      isEmpty(context.ppp?.firstBeneficiary?.firstName)
    ) {
      if (errorsList.indexOf("firstFirstName") === -1)
        errorsList.push("firstFirstName");
    }

    if (
      context.ppp?.withBeneficiary &&
      isEmpty(context.ppp?.firstBeneficiary?.lastName)
    ) {
      if (errorsList.indexOf("firstLastName") === -1)
        errorsList.push("firstLastName");
    }

    if (
      context.ppp?.withBeneficiary &&
      isEmpty(context.ppp?.firstBeneficiary?.birthDate) === false &&
      !context.ppp?.firstBeneficiary?.birthDate?.match(
        new RegExp(/\b[0-9][0-9][0-9][0-9]-[0-9][0-9]-[0-9][0-9]\b/),
      )
    ) {
      if (errorsList.indexOf("firstBirthName") === -1)
        errorsList.push("firstBirthName");
    }

    if (
      context.ppp?.withBeneficiary &&
      context.ppp.firstBeneficiary?.issuingCountry?.name?.toLocaleLowerCase() ===
        "polska"
    ) {
      switch (context.ppp.firstBeneficiary?.documentType?.name?.toLowerCase()) {
        case "dowód osobisty":
          if (
            context.ppp.firstBeneficiary.seriesAndNumber?.match(
              new RegExp(/\b[A-Z][A-Z][A-Z][0-9][0-9][0-9][0-9][0-9][0-9]\b/),
            ) == null ||
            validateDO(context.ppp.firstBeneficiary.seriesAndNumber) !== true
          ) {
            if (errorsList.indexOf("firstSeriesAndNumber") === -1)
              errorsList.push("firstSeriesAndNumber");
          }
          break;
        case "paszport":
          if (
            context.ppp.firstBeneficiary.seriesAndNumber?.match(
              new RegExp(/\b[A-Z][A-Z][0-9][0-9][0-9][0-9][0-9][0-9][0-9]\b/),
            ) == null ||
            validatePassport(context.ppp.firstBeneficiary.seriesAndNumber) !==
              true
          ) {
            if (errorsList.indexOf("firstSeriesAndNumber") === -1)
              errorsList.push("firstSeriesAndNumber");
          }
          break;
        case "mdowód":
          if (
            context.ppp.firstBeneficiary.seriesAndNumber?.match(
              new RegExp(/\bM[A-Z][A-Z][A-Z][0-9][0-9][0-9][0-9][0-9]\b/),
            ) == null
          ) {
            if (errorsList.indexOf("firstSeriesAndNumber") === -1)
              errorsList.push("firstSeriesAndNumber");
          }
          break;
      }
    }

    if (!isEmpty(context?.ppp?.firstBeneficiary?.documentType?.name)) {
      if (isEmpty(context?.ppp?.firstBeneficiary?.issuingCountry?.name)) {
        if (errorsList.indexOf("firstIssuingCountry") === -1)
          errorsList.push("firstIssuingCountry");
      }

      if (isEmpty(context?.ppp?.firstBeneficiary?.seriesAndNumber)) {
        if (errorsList.indexOf("firstSeriesAndNumber") === -1)
          errorsList.push("firstSeriesAndNumber");
      }
    }

    if (
      context.ppp?.withBeneficiary &&
      context.ppp.firstBeneficiary != null &&
      context.ppp.firstBeneficiary?.option == null
    ) {
      if (errorsList.indexOf("firstOption") === -1)
        errorsList.push("firstOption");
    }

    if (
      context.ppp?.withBeneficiary &&
      context.ppp.firstBeneficiary?.option != null &&
      context.ppp.firstBeneficiary?.option != 0 &&
      isEmpty(context.ppp.firstBeneficiary.pppText)
    ) {
      if (errorsList.indexOf("firstOptionName") === -1)
        errorsList.push("firstOptionName");
    }

    if (
      context.ppp?.withBeneficiary &&
      context.ppp.secondBeneficiary != null &&
      isEmpty(context.ppp?.secondBeneficiary?.firstName)
    ) {
      if (errorsList.indexOf("secondFirstName") === -1)
        errorsList.push("secondFirstName");
    }

    if (
      context.ppp?.withBeneficiary &&
      context.ppp.secondBeneficiary != null &&
      isEmpty(context.ppp?.secondBeneficiary?.lastName)
    ) {
      if (errorsList.indexOf("secondLastName") === -1)
        errorsList.push("secondLastName");
    }

    if (
      context.ppp?.withBeneficiary &&
      context.ppp.secondBeneficiary != null &&
      isEmpty(context.ppp?.secondBeneficiary?.birthDate) === false &&
      !context.ppp?.secondBeneficiary?.birthDate?.match(
        new RegExp(/\b[0-9][0-9][0-9][0-9]-[0-9][0-9]-[0-9][0-9]\b/),
      )
    ) {
      if (errorsList.indexOf("secondBirthName") === -1)
        errorsList.push("secondBirthName");
    }

    if (
      context.ppp?.withBeneficiary &&
      context.ppp.secondBeneficiary?.issuingCountry?.name?.toLocaleLowerCase() ===
        "polska"
    ) {
      switch (
        context.ppp.secondBeneficiary?.documentType?.name?.toLowerCase()
      ) {
        case "dowód osobisty":
          if (
            context.ppp.secondBeneficiary.seriesAndNumber?.match(
              new RegExp(/\b[A-Z][A-Z][A-Z][0-9][0-9][0-9][0-9][0-9][0-9]\b/),
            ) == null ||
            validateDO(context.ppp.secondBeneficiary.seriesAndNumber) !== true
          ) {
            if (errorsList.indexOf("secondSeriesAndNumber") === -1)
              errorsList.push("secondSeriesAndNumber");
          }
          break;
        case "paszport":
          if (
            context.ppp.secondBeneficiary.seriesAndNumber?.match(
              new RegExp(/\b[A-Z][A-Z][0-9][0-9][0-9][0-9][0-9][0-9][0-9]\b/),
            ) == null ||
            validatePassport(context.ppp.secondBeneficiary.seriesAndNumber) !==
              true
          ) {
            if (errorsList.indexOf("secondSeriesAndNumber") === -1)
              errorsList.push("secondSeriesAndNumber");
          }
          break;
        case "mdowód":
          if (
            context.ppp.secondBeneficiary.seriesAndNumber?.match(
              new RegExp(/\bM[A-Z][A-Z][A-Z][0-9][0-9][0-9][0-9][0-9]\b/),
            ) == null
          ) {
            if (errorsList.indexOf("secondSeriesAndNumber") === -1)
              errorsList.push("secondSeriesAndNumber");
          }
          break;
      }
    }

    if (!isEmpty(context?.ppp?.secondBeneficiary?.documentType?.name)) {
      if (isEmpty(context?.ppp?.secondBeneficiary?.issuingCountry?.name)) {
        if (errorsList.indexOf("secondIssuingCountry") === -1)
          errorsList.push("secondIssuingCountry");
      }

      if (isEmpty(context?.ppp?.secondBeneficiary?.seriesAndNumber)) {
        if (errorsList.indexOf("secondSeriesAndNumber") === -1)
          errorsList.push("secondSeriesAndNumber");
      }
    }

    if (
      context.ppp?.withBeneficiary &&
      context.ppp.secondBeneficiary != null &&
      context.ppp.secondBeneficiary?.option == null
    ) {
      if (errorsList.indexOf("secondOption") === -1)
        errorsList.push("secondOption");
    }

    if (
      context.ppp?.withBeneficiary &&
      context.ppp.secondBeneficiary?.option != null &&
      context.ppp.secondBeneficiary?.option != 0 &&
      isEmpty(context.ppp.secondBeneficiary.pppText)
    ) {
      if (errorsList.indexOf("secondOptionName") === -1)
        errorsList.push("secondOptionName");
    }

    if (
      context.ppp?.withBeneficiary &&
      context.ppp.thirdBeneficiary != null &&
      isEmpty(context.ppp?.thirdBeneficiary?.firstName)
    ) {
      if (errorsList.indexOf("thirdFirstName") === -1)
        errorsList.push("thirdFirstName");
    }

    if (
      context.ppp?.withBeneficiary &&
      context.ppp.thirdBeneficiary != null &&
      isEmpty(context.ppp?.thirdBeneficiary?.lastName)
    ) {
      if (errorsList.indexOf("thirdLastName") === -1)
        errorsList.push("thirdLastName");
    }

    if (
      context.ppp?.withBeneficiary &&
      context.ppp.thirdBeneficiary != null &&
      isEmpty(context.ppp?.thirdBeneficiary?.birthDate) === false &&
      !context.ppp?.thirdBeneficiary?.birthDate?.match(
        new RegExp(/\b[0-9][0-9][0-9][0-9]-[0-9][0-9]-[0-9][0-9]\b/),
      )
    ) {
      if (errorsList.indexOf("thirdBirthName") === -1)
        errorsList.push("thirdBirthName");
    }

    if (
      context.ppp?.withBeneficiary &&
      context.ppp.thirdBeneficiary?.issuingCountry?.name?.toLocaleLowerCase() ===
        "polska"
    ) {
      switch (context.ppp.thirdBeneficiary?.documentType?.name?.toLowerCase()) {
        case "dowód osobisty":
          if (
            context.ppp.thirdBeneficiary.seriesAndNumber?.match(
              new RegExp(/\b[A-Z][A-Z][A-Z][0-9][0-9][0-9][0-9][0-9][0-9]\b/),
            ) == null ||
            validateDO(context.ppp.thirdBeneficiary.seriesAndNumber) !== true
          ) {
            if (errorsList.indexOf("thirdSeriesAndNumber") === -1)
              errorsList.push("thirdSeriesAndNumber");
          }
          break;
        case "paszport":
          if (
            context.ppp.thirdBeneficiary.seriesAndNumber?.match(
              new RegExp(/\b[A-Z][A-Z][0-9][0-9][0-9][0-9][0-9][0-9][0-9]\b/),
            ) == null ||
            validatePassport(context.ppp.thirdBeneficiary.seriesAndNumber) !==
              true
          ) {
            if (errorsList.indexOf("thirdSeriesAndNumber") === -1)
              errorsList.push("thirdSeriesAndNumber");
          }
          break;
        case "mdowód":
          if (
            context.ppp.thirdBeneficiary.seriesAndNumber?.match(
              new RegExp(/\bM[A-Z][A-Z][A-Z][0-9][0-9][0-9][0-9][0-9]\b/),
            ) == null
          ) {
            if (errorsList.indexOf("thirdSeriesAndNumber") === -1)
              errorsList.push("thirdSeriesAndNumber");
          }
          break;
      }
    }

    if (!isEmpty(context?.ppp?.secondBeneficiary?.documentType?.name)) {
      if (isEmpty(context?.ppp?.secondBeneficiary?.issuingCountry?.name)) {
        if (errorsList.indexOf("thirdIssuingCountry") === -1)
          errorsList.push("thirdIssuingCountry");
      }

      if (isEmpty(context?.ppp?.secondBeneficiary?.seriesAndNumber)) {
        if (errorsList.indexOf("thirdSeriesAndNumber") === -1)
          errorsList.push("thirdSeriesAndNumber");
      }
    }

    if (
      context.ppp?.withBeneficiary &&
      context.ppp.thirdBeneficiary != null &&
      context.ppp.thirdBeneficiary?.option == null
    ) {
      if (errorsList.indexOf("thirdOption") === -1)
        errorsList.push("thirdOption");
    }

    if (
      context.ppp?.withBeneficiary &&
      context.ppp.thirdBeneficiary?.option != null &&
      context.ppp.thirdBeneficiary?.option != 0 &&
      isEmpty(context.ppp.thirdBeneficiary.pppText)
    ) {
      if (errorsList.indexOf("thirdOptionName") === -1)
        errorsList.push("thirdOptionName");
    }

    setBeneficiaryErrors(errorsList);

    return errorsList;
  };

  const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));

  const handleOnClickNext = async () => {
    setNextClicked(true);
    const check = checkErrors(true);
    const beneficiary = checkBeneficiary();

    await trigger("profession");

    if (
      check.length === 0 &&
      beneficiary.length === 0 &&
      getValues("profession") != null
    ) {
      const localData = {
        survey: pppData,
        surveyHighRisk: highRiskPppData,
        isHighRisk: isHighRisk,
        withBeneficiary: context.ppp?.withBeneficiary,
        firstBeneficiary: context.ppp?.firstBeneficiary,
        secondBeneficiary: context.ppp?.secondBeneficiary,
        thirdBeneficiary: context.ppp?.thirdBeneficiary,
      };

      const { success } = await sendLoadingPostRequest<PPPType, boolean>({
        loadingFunction: setLoading,
        endpoint: "Agreement/SendPPPAml",
        params: {
          surveyList: localData,
          profession: getValues("profession"),
        },
      });

      if (success) {
        if (context.setPPP) {
          context.setPPP(localData);
        }
        if (context.setStep2 && context.step2) {
          context.setStep2({
            ...context.step2,
            profession: getValues("profession"),
          });
        }

        const { data: response, success: successHighRisk } =
          await sendLoadingPostRequest<PPPType, CheckHighRisk>({
            loadingFunction: setLoading,
            endpoint: "agreement/CheckHighRisk",
            params: {
              surveyList: localData,
              profession: getValues("profession"),
            },
          });

        if (response?.data.forceHighRisk == true) {
          if (context.setPPP) {
            context.setPPP((prev) =>
              prev != null
                ? {
                    ...prev,
                  }
                : localData,
            );
          }
          setStep(4);
        } else if (response?.data.check == false) {
          if (context.ppp?.withBeneficiary === true) {
            if (context.setPPP) {
              context.setPPP((prev) =>
                prev != null
                  ? {
                      ...prev,
                      isHighRisk: true,
                      checkHighRisk: response?.data,
                    }
                  : localData,
              );
            }
            setStep(8);
          } else {
            if (context.setPPP) {
              context.setPPP((prev) =>
                prev != null
                  ? {
                      ...prev,
                    }
                  : localData,
              );
            }
            setStep(4);
          }
        } else {
          let highRisk = context.ppp?.withBeneficiary === true;
          if (response?.data.check === true) highRisk = true;

          if (context.setPPP) {
            context.setPPP((prev) =>
              prev != null
                ? {
                    ...prev,
                    isHighRisk: highRisk,
                    checkHighRisk: response?.data,
                  }
                : localData,
            );
          }
          setStep(8);
        }
      }
    } else {
      if (errors.profession) {
        scrollToElement(`profession`);
        return;
      }
      if (check && check.length > 0) {
        const firstId = check[0];
        scrollToElement(`Question-${firstId}`);
        return;
      }

      if (!beneficiary || beneficiary.length === 0) return;

      const firstbeneficiaryId = beneficiary[0];
      scrollToElement(firstbeneficiaryId);
    }
  };

  //getPPP();

  useEffect(() => {
    window.scrollTo(0, 0);
    if (context.ppp == null) {
      getPPP();
    } else {
      setPppData(context.ppp.survey);
      setHighRiskPppData(context.ppp.surveyHighRisk);
      setIsHighRisk(context.ppp.isHighRisk);

      setValue("profession", context.step2?.profession);
    }
    getDictionary();
  }, []);

  useEffect(() => {
    checkErrors(false);
  }, [pppData]);

  return (
    <ThemeProvider theme={defaultMaterialTheme}>
      <hr />

      <p className="bold red section mb-4">
        Formularz danych dotyczący przeciwdziałaniu praniu pieniędzy
      </p>

      <p>
        Podstawa prawna: ustawa z dnia 1 marca 2018 r. o przeciwdziałaniu praniu
        pieniędzy oraz finansowaniu terroryzmu („Ustawa”)
      </p>
      <p>
        Zgodnie z wymogami Ustawy prosimy o przedstawienie danych, które są
        niezbędne Domowi Inwestycyjnemu Xelion sp. z o.o. („DI Xelion”) do
        zastosowania środków bezpieczeństwa finansowego.
      </p>

      <div id="emailContainer" className="row px-3 mt-4 mb-4">
        <div className="form-group mt-1 mb-1">
          <Controller
            render={({ field }) => (
              <>
                <Autocomplete
                  ref={field.ref}
                  options={
                    dictionary?.professions.sort(compareDictionary) ?? []
                  }
                  value={field.value}
                  onChange={(event: any, newValue: DictionaryCombo | null) => {
                    field.onChange(newValue);
                  }}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  renderInput={(params) => (
                    <TextFieldStyled
                      {...params}
                      error={errors.profession != null}
                      name="profession"
                      label="Zawód"
                      variant="outlined"
                      size="small"
                    />
                  )}
                  noOptionsText={"Brak danych"}
                />
              </>
            )}
            name="profession"
            control={control}
            rules={{
              required: "Zawód jest wymagany",
            }}
          />
          {errors.profession && (
            <span className="validationError" role="alert">
              <i className="fa fa-exclamation-circle" />
              Zawód jest wymagany
            </span>
          )}
        </div>
      </div>

      {pppData?.questions?.map((question) => (
        <GenerateQuestion
          key={question.id}
          question={question}
          pppData={setPppData}
          errorIds={errorIds}
        />
      ))}

      <Beneficiary
        errors={beneficiaryErrors}
        setErrors={setBeneficiaryErrors}
      />

      <div className="row px-3 mb-3 mt-4">
        <div className="col-sm-12 code">
          <div className="mr-12 float-right">
            <button
              onClick={() => {
                if (context.setPPP) {
                  context.setPPP({
                    survey: pppData,
                    surveyHighRisk: highRiskPppData,
                    isHighRisk: isHighRisk,
                    withBeneficiary: context.ppp?.withBeneficiary,
                    firstBeneficiary: context.ppp?.firstBeneficiary,
                    secondBeneficiary: context.ppp?.secondBeneficiary,
                    thirdBeneficiary: context.ppp?.thirdBeneficiary,
                  });

                  if (context.setStep2 && context.step2) {
                    context.setStep2({
                      ...context.step2,
                      profession: getValues("profession"),
                    });
                  }
                }
                setStep(3);
              }}
              className="backBtn"
              type="submit"
            >
              Cofnij
            </button>
            <input
              onClick={handleOnClickNext}
              className="nextBtn"
              type="submit"
              value="Dalej"
            />
          </div>
        </div>
      </div>

      {loading && <LoadingPanel />}
    </ThemeProvider>
  );
};

export { StepPPP };
